import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { supabase, supabasePublic } from '@/supabase/client'
import { useAuth } from '../lib/auth-context'
import { roleToPath } from '@/lib/role-redirect'
import * as webauthn from '@simplewebauthn/browser'

const CORE_API = import.meta.env.VITE_CORE_API_URL || ''

const roles = [
  { label: 'Nominee', value: 'nominee' },
  { label: 'Sponsor', value: 'sponsor' },
  { label: 'Teacher', value: 'teacher' },
  { label: 'Coach', value: 'coach' },
  { label: 'Prospect', value: 'prospect' },
  { label: 'Guardian', value: 'guardian' },
  { label: 'Admin', value: 'admin' },
]

const defaultPortalSettings = {
  sponsor: 'open',
  teacher: 'open',
  nominee: 'open',
  prospect: 'open',
  guardian: 'open',
  student: 'open',
  coach: 'open',
}

const formatRoleLabel = (value: string | null | undefined): string => {
  if (!value) return ''
  const match = roles.find(option => option.value === value)
  if (match) return match.label
  return value.charAt(0).toUpperCase() + value.slice(1)
}

export default function Login() {
  const navigate = useNavigate()
  const location = useLocation()
  const { search } = location
  const params = useMemo(() => new URLSearchParams(search), [search])
  const redirect = useMemo(() => {
    // Accept both `redirect` (legacy) and `next` (RequireAuth convention)
    const value = params.get('redirect') || params.get('next')
    if (value && value.startsWith('/')) return value
    return ''
  }, [params])
  const notice = params.get('notice')
  const requiredRoleParam = params.get('required')
  const currentRoleParam = params.get('current')
  const toastRoleParam = params.get('toastRole')
  const roleQueryParam = useMemo(() => {
    const value = params.get('role')
    return roles.some(option => option.value === value) ? value : null
  }, [params])

  const {
    session,
    profile,
    status: authStatus,
    fetchProfile,
    rememberRole,
    signOut,
    error: profileError,
  } = useAuth()

  const [selectedRole, setSelectedRole] = useState(() => roleQueryParam || 'sponsor')
  const [email, setEmail] = useState('')
  // Pool shortcut state — auto-detects role from email
  const [poolEmail, setPoolEmail] = useState('')
  const [poolStatus, setPoolStatus] = useState<'idle' | 'looking' | 'sending' | 'sent' | 'not-found'>('idle')
  const [poolMessage, setPoolMessage] = useState('')
  const [password, setPassword] = useState('')
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')
  const [sendingLink, setSendingLink] = useState(false)
  const [passwordLoading, setPasswordLoading] = useState(false)
  const [signingOut, setSigningOut] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const enablePasskeys = import.meta.env?.VITE_ENABLE_PASSKEYS === 'true'
  const [registeringPasskey, setRegisteringPasskey] = useState(false)
  const [passkeySigningIn, setPasskeySigningIn] = useState(false)
  const [passkeyMessage, setPasskeyMessage] = useState('')
  const roleParamRef = useRef(roleQueryParam)
  const [portalSettings, setPortalSettings] = useState(defaultPortalSettings)

  // Canonical base → /auth/callback (prod via VITE_SITE_URL, fallback to origin)
  const redirectBase = useMemo(
    () => import.meta.env.VITE_SITE_URL || (typeof window !== 'undefined' ? window.location.origin : ''),
    [],
  )
  const authCallbackUrl = useMemo(() => {
    if (!redirectBase) return ''
    return `${redirectBase.replace(/\/$/, '')}/auth/callback`
  }, [redirectBase])

  useEffect(() => {
    if (roleQueryParam && roleParamRef.current !== roleQueryParam) {
      setSelectedRole(roleQueryParam)
      roleParamRef.current = roleQueryParam
    }
    if (!roleQueryParam) roleParamRef.current = null
  }, [roleQueryParam])

  const mismatchMessage = useMemo(() => {
    if (notice !== 'role-mismatch') return ''
    const requiredLabel = formatRoleLabel(requiredRoleParam)
    const portalLabel = requiredLabel ? `The ${requiredLabel} portal` : 'This portal'
    const accountDescriptor = requiredLabel ? `${requiredLabel.toLowerCase()} accounts` : 'the requested account type'
    const base = `${portalLabel} is reserved for ${accountDescriptor}.`
    if (currentRoleParam) {
      const currentLabel = formatRoleLabel(currentRoleParam)
      if (currentLabel) {
        return `${base} You're currently signed in as a ${currentLabel.toLowerCase()} user. Please choose the correct portal or sign out to switch roles.`
      }
    }
    return `${base} Please choose the correct portal or sign out to switch roles.`
  }, [currentRoleParam, notice, requiredRoleParam])

  const safeCurrentRole = useMemo(
    () => (roles.some(option => option.value === currentRoleParam) ? currentRoleParam : ''),
    [currentRoleParam],
  )

  useEffect(() => {
    if (!toastRoleParam) {
      setToastMessage('')
      return
    }
    const label = formatRoleLabel(toastRoleParam)
    setToastMessage(label ? `This area is for ${label.toLowerCase()} accounts.` : 'This area is restricted to specific accounts.')
  }, [toastRoleParam])

  useEffect(() => {
    if (!toastMessage || typeof window === 'undefined') return undefined
    const timeout = window.setTimeout(() => setToastMessage(''), 6000)
    return () => window.clearTimeout(timeout)
  }, [toastMessage])

  const portalIsOpen = useCallback(
    (role: string | null | undefined): boolean => {
      if (!role || role === 'admin') return true
      const mode = portalSettings?.[role as keyof typeof portalSettings]
      if (!mode) return true
      return String(mode).toLowerCase() === 'open'
    },
    [portalSettings],
  )

  useEffect(() => {
    let cancelled = false

    const loadPortalSettings = async () => {
      try {
        const response = await fetch(`${CORE_API}/api/v1/public/portals/access`)
        const payload = await response.json().catch(() => ({}))
        if (!cancelled && response.ok && payload?.settings) {
          setPortalSettings((prev) => ({ ...prev, ...payload.settings }))
        }
      } catch (error) {
        if (!cancelled) {
          console.warn('[login] portal settings fetch failed', error)
        }
      }
    }

    loadPortalSettings()
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (portalIsOpen(selectedRole)) return
    const fallback = roles.find(option => portalIsOpen(option.value))?.value
    if (fallback && fallback !== selectedRole) {
      setSelectedRole(fallback)
    }
  }, [portalIsOpen, selectedRole])

  const hasPortalAccess = Boolean(session?.access_token && profile?.role)
  const holdPortalAccess = notice === 'role-mismatch'

  useEffect(() => {
    if (!hasPortalAccess || holdPortalAccess) return

    const defaultDestination = roleToPath(profile?.role ?? '')
    const target = redirect || defaultDestination

    if (!target) return
    if (location.pathname === target) return

    navigate(target, { replace: true })
  }, [hasPortalAccess, holdPortalAccess, location.pathname, navigate, profile?.role, redirect])
 
  const handleRoleSelect = (value: string): void => {
    if (!portalIsOpen(value)) {
      setError('This portal is currently closed. Please check back soon.')
      setMessage('')
      return
    }
    setSelectedRole(value)
    setError('')
    setMessage('')
    if (value !== 'admin') {
      setPassword('')
    }
  }

  const handleReturnToPortal = () => {
    if (!safeCurrentRole) return
    navigate(roleToPath(safeCurrentRole || 'sponsor'))
  }

  const handleSignOutNotice = async () => {
    setSigningOut(true)
    try {
      await signOut()
      navigate('/login', { replace: true })
    } catch (err) {
      console.error('Sign out failed', err)
    } finally {
      setSigningOut(false)
    }
  }

  const handleMagicLink = async () => {
    if (!email) {
      setError('Please enter your email address.')
      return
    }
    if (!portalIsOpen(selectedRole)) {
      setError('This portal is currently closed. Please check back soon.')
      setMessage('')
      return
    }
    setError('')
    setMessage('')
    setSendingLink(true)
    rememberRole(selectedRole)

    try {
      const nextTarget = redirect || roleToPath(selectedRole || 'sponsor')
      const redirectTo = authCallbackUrl
        ? `${authCallbackUrl}?next=${encodeURIComponent(nextTarget)}`
        : undefined

      const normalizedRole = (selectedRole || '').toLowerCase()

      const response = await fetch(`${import.meta.env.VITE_CORE_API_URL || ''}/api/v1/auth/request-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          role: normalizedRole,
          redirectTo,
        }),
      })

      if (!response.ok) {
        const payload = await response.json().catch(() => ({}))
        const errMsg =
          typeof payload?.error === 'string'
            ? payload.error
            : payload?.error?.message
        if (response.status === 403) {
          setError(errMsg || 'Not authorized')
          return
        }
        setError(errMsg || 'Could not send link. Try again.')
        return
      }

      setMessage('Check your email for a secure sign-in link.')
    } catch (linkError: unknown) {
      console.error('Magic link error (Render)', linkError)
      // Fallback: send magic link directly via Supabase when Render is unreachable
      try {
        const nextTarget = redirect || roleToPath(selectedRole || 'sponsor')
        const fallbackRedirectTo = authCallbackUrl
          ? `${authCallbackUrl}?next=${encodeURIComponent(nextTarget)}`
          : undefined

        const { error: otpError } = await supabase.auth.signInWithOtp({
          email,
          options: {
            emailRedirectTo: fallbackRedirectTo,
            shouldCreateUser: true,
          },
        })

        if (otpError) {
          setError(otpError.message || 'Could not send link. Try again.')
        } else {
          setMessage('Check your email for a secure sign-in link.')
        }
      } catch (fallbackError: unknown) {
        console.error('Magic link fallback error', fallbackError)
        setError((fallbackError as Error)?.message || 'Could not send link. Try again.')
      }
    } finally {
      setSendingLink(false)
    }
  }

  // Pool shortcut: email-first login that auto-detects role
  const handlePoolSignIn = async () => {
    const cleaned = poolEmail.trim().toLowerCase()
    if (!cleaned) {
      setPoolMessage('Please enter your email.')
      return
    }

    setPoolStatus('looking')
    setPoolMessage('')

    try {
      // Look up the user's role
      const lookupRes = await fetch(`${import.meta.env.VITE_CORE_API_URL || ''}/api/v1/auth/lookup-role`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: cleaned }),
      })

      const lookup = await lookupRes.json().catch(() => ({}))
      const detectedRole = lookup?.data?.role as string | undefined

      if (!detectedRole) {
        setPoolStatus('not-found')
        setPoolMessage("We couldn't find you in The Pool. Ask someone to nominate you, or confirm your existing nomination first.")
        return
      }

      // Send magic link with the detected role
      setPoolStatus('sending')
      const nextTarget = redirect || '/portal/nominee'
      const redirectTo = authCallbackUrl
        ? `${authCallbackUrl}?next=${encodeURIComponent(nextTarget)}`
        : undefined

      const linkRes = await fetch(`${import.meta.env.VITE_CORE_API_URL || ''}/api/v1/auth/request-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: cleaned, role: detectedRole, redirectTo }),
      })

      if (!linkRes.ok) {
        const payload = await linkRes.json().catch(() => ({}))
        const errMsg = typeof payload?.error === 'string' ? payload.error : payload?.error?.message
        setPoolStatus('idle')
        setPoolMessage(errMsg || 'Could not send link. Try again.')
        return
      }

      setPoolStatus('sent')
      setPoolMessage(`Magic link sent! Check ${cleaned} to sign in as ${detectedRole}.`)
    } catch (err) {
      console.error('[pool signin]', err)
      setPoolStatus('idle')
      setPoolMessage('Something went wrong. Try again or use the role picker below.')
    }
  }

  const handlePasswordSignIn = async () => {
    if (!email) {
      setError('Please enter your email address.')
      return
    }
    if (!password) {
      setError('Please enter your password.')
      return
    }

    setError('')
    setMessage('')
    setPasswordLoading(true)
    rememberRole('admin')

    try {
      const {
        data: { user },
        error: authError,
      } = await supabase.auth.signInWithPassword({ email, password })

      if (authError || !user) {
        throw authError || new Error('Unable to sign in with password.')
      }

      let {
        data: isAdmin,
        error: isAdminError,
      } = await (supabasePublic.rpc as Function)('is_admin', { p_user: user.id })

      if (isAdminError) {
        console.warn('Public schema admin check failed', isAdminError)
        const fallback = await (supabase.rpc as Function)('is_admin', { p_user: user.id })
        isAdmin = fallback.data
        isAdminError = fallback.error
      }

      if (isAdminError) {
        throw new Error('Unable to verify access.')
      }

      if (!isAdmin) {
        await supabase.auth.signOut()
        setPassword('')
        setError('This account is not authorized for Admin access.')
        return
      }

      const {
        data: { session: activeSession },
        error: sessionError,
      } = await supabase.auth.getSession()

      if (sessionError || !activeSession?.access_token) {
        throw sessionError || new Error('Unable to retrieve session token.')
      }

      try {
        await fetchProfile(activeSession)
      } catch (profileFetchError: unknown) {
        console.warn('Unable to refresh profile after sign-in', profileFetchError)
      }

      setMessage('Signed in! Use the button above to enter the portal.')
    } catch (passwordError: unknown) {
      console.error('Password sign-in error', passwordError)
      setError((passwordError as Error)?.message || 'Unable to sign in with password.')
    } finally {
      setPasswordLoading(false)
    }
  }

  const normalizedEmail = () => email.trim().toLowerCase()

  const handleRegisterPasskey = async () => {
    if (!email) {
      setError('Please enter your email address.')
      return
    }
    const targetEmail = normalizedEmail()
    if (!targetEmail) {
      setError('Please enter your email address.')
      return
    }
    if (!portalIsOpen(selectedRole)) {
      setError('This portal is currently closed. Please check back soon.')
      setMessage('')
      return
    }
    setError('')
    setMessage('')
    setPasskeyMessage('')
    setRegisteringPasskey(true)

    try {
      const startResponse = await fetch(`${CORE_API}/api/v1/auth/webauthn/register-start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: targetEmail }),
      })

      const startData = await startResponse.json()
      if (!startResponse.ok) {
        throw new Error(startData?.error || 'Unable to start passkey registration.')
      }

      const registration = await webauthn.startRegistration(startData)

      const finishResponse = await fetch(`${CORE_API}/api/v1/auth/webauthn/register-finish`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: targetEmail, response: registration }),
      })

      const finishData = await finishResponse.json()
      if (!finishResponse.ok || !finishData?.ok) {
        throw new Error(finishData?.error || 'Unable to save your passkey.')
      }

      setPasskeyMessage('Passkey saved! You can now sign in with Face ID.')
    } catch (passkeyError: unknown) {
      if ((passkeyError as Error)?.name === 'NotAllowedError') {
        setPasskeyMessage('Passkey setup was canceled.')
        return
      }
      console.error('Passkey registration error', passkeyError)
      setError((passkeyError as Error)?.message || 'Unable to register passkey.')
    } finally {
      setRegisteringPasskey(false)
    }
  }

  const handlePasskeySignIn = async () => {
    if (!email) {
      setError('Please enter your email address.')
      return
    }
    const targetEmail = normalizedEmail()
    if (!targetEmail) {
      setError('Please enter your email address.')
      return
    }
    if (!portalIsOpen(selectedRole)) {
      setError('This portal is currently closed. Please check back soon.')
      setMessage('')
      return
    }
    setError('')
    setMessage('')
    setPasskeyMessage('')
    setPasskeySigningIn(true)

    try {
      const startResponse = await fetch(`${CORE_API}/api/v1/auth/webauthn/login-start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: targetEmail }),
      })

      const startData = await startResponse.json()
      if (!startResponse.ok) {
        throw new Error(startData?.error || 'Unable to start passkey sign-in.')
      }

      const assertion = await webauthn.startAuthentication(startData)

      const finishResponse = await fetch(`${CORE_API}/api/v1/auth/webauthn/login-finish`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: targetEmail, response: assertion }),
      })

      const finishData = await finishResponse.json()
      if (!finishResponse.ok || !finishData?.otp) {
        throw new Error(finishData?.error || 'Unable to verify passkey.')
      }

      const otpEmail = finishData.email || targetEmail
      const { data: otpData, error: otpError } = await supabase.auth.verifyOtp({
        email: otpEmail,
        token: finishData.otp,
        type: 'magiclink',
      })

      if (otpError || !otpData?.session) {
        throw otpError || new Error('Unable to create a Supabase session.')
      }

      const activeSession = otpData.session
      rememberRole('sponsor')

      try {
        await fetchProfile(activeSession)
      } catch (profileFetchError: unknown) {
        console.warn('Unable to refresh profile after passkey sign-in', profileFetchError)
      }

      setPasskeyMessage('Signed in with Face ID! Use the button above to enter the portal.')
    } catch (passkeyError: unknown) {
      if ((passkeyError as Error)?.name === 'NotAllowedError') {
        setPasskeyMessage('Passkey request was canceled.')
      } else {
        console.error('Passkey login error', passkeyError)
        setError((passkeyError as Error)?.message || 'Unable to sign in with Face ID.')
      }
    } finally {
      setPasskeySigningIn(false)
    }
  }

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault()
    if (selectedRole === 'admin') {
      handlePasswordSignIn()
    } else {
      handleMagicLink()
    }
  }

  if (authStatus === 'checking') {
    return <div className="p-8 text-center text-cocoa/70">Loading…</div>
  }

  // EARLY RETURN — Already authenticated → show portal button
  if (hasPortalAccess && !holdPortalAccess) {
    const portalPath = roleToPath(profile?.role ?? '')

    return (
      <div className="min-h-screen bg-shell flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
          <h2 className="text-2xl font-bold text-cocoa mb-4">
            Welcome back!
          </h2>
          <p className="text-cocoa/70 mb-6">
            You're signed in as a {profile?.role}.
          </p>

          <div className="relative">
            <button
              onClick={() => navigate(portalPath)}
              className="group relative w-full overflow-hidden rounded-2xl bg-gradient-to-r from-terracotta via-brick to-mint text-white py-4 text-lg font-semibold shadow-xl transition hover:brightness-110 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/80"
            >
              <span
                aria-hidden="true"
                className="pointer-events-none absolute inset-y-0 left-0 w-24 bg-[url('/logo.png')] bg-contain bg-left bg-no-repeat opacity-20 transition-opacity group-hover:opacity-40"
              />
              <span className="relative z-10 flex items-center justify-center gap-3">
                <img src="/logo.png" alt="" className="h-9 w-9 drop-shadow" aria-hidden="true" />
                <span>
                  Enter {profile?.role === 'teacher' ? 'Teacher' : profile?.role === 'sponsor' ? 'Sponsor' : 'Admin'} Portal
                </span>
                <span aria-hidden="true" className="text-2xl font-bold leading-none">
                  →
                </span>
              </span>
            </button>
          </div>

          <button
            onClick={handleSignOutNotice}
            disabled={signingOut}
            className="mt-4 text-sm text-cocoa/60 hover:text-cocoa"
          >
            {signingOut ? 'Signing out...' : 'Sign out'}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="relative max-w-lg mx-auto w-full">
      {toastMessage && (
        <div className="pointer-events-none fixed inset-x-0 top-4 flex justify-center px-4 sm:justify-end sm:pr-8">
          <div className="pointer-events-auto flex items-start gap-3 rounded-2xl bg-cocoa text-white shadow-soft px-4 py-3">
            <span className="text-sm leading-relaxed">{toastMessage}</span>
            <button
              type="button"
              onClick={() => setToastMessage('')}
              className="text-white/70 hover:text-white transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
              aria-label="Dismiss notification"
            >
              ×
            </button>
          </div>
        </div>
      )}

      <div className="bg-shell rounded-3xl shadow-soft p-6 md:p-8 space-y-6">
        <div className="space-y-2 text-center">
          <img src="/logo.png" alt="Project Tower logo" className="h-10 w-auto mx-auto" />
          <h1 className="text-2xl font-semibold text-cocoa">Access your Project Tower portal</h1>
          <p className="text-sm text-cocoa/70">Sponsors &amp; Teachers use email link. Admins use password.</p>
        </div>

        {/* Pool shortcut — email-first login for nominees */}
        <div className="rounded-2xl border-2 border-cocoa/20 bg-gradient-to-br from-cocoa/5 to-white p-5 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-cocoa/50">Nominees</p>
              <h2 className="text-base font-bold text-cocoa">Enter The Pool</h2>
            </div>
            <span className="text-2xl" role="img" aria-label="pool">🎯</span>
          </div>
          <p className="text-xs text-cocoa/60">
            Confirmed teacher, coach, or prospect? Just enter your email — we'll detect your role automatically.
          </p>

          {poolStatus === 'sent' ? (
            <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-800">
              {poolMessage}
            </div>
          ) : (
            <form
              onSubmit={(e) => { e.preventDefault(); handlePoolSignIn() }}
              className="space-y-2"
            >
              <input
                type="email"
                required
                autoComplete="email"
                className="w-full rounded-xl border border-sand bg-white px-3 py-2 text-sm focus:border-cocoa focus:outline-none"
                placeholder="you@school.edu"
                value={poolEmail}
                onChange={(e) => setPoolEmail(e.target.value)}
                disabled={poolStatus === 'looking' || poolStatus === 'sending'}
              />
              {poolMessage && poolStatus !== 'sent' && (
                <p className={`text-xs ${poolStatus === 'not-found' ? 'text-brick' : 'text-cocoa/60'}`}>
                  {poolMessage}
                </p>
              )}
              <button
                type="submit"
                disabled={!poolEmail.trim() || poolStatus === 'looking' || poolStatus === 'sending'}
                className="w-full rounded-xl bg-cocoa px-4 py-2.5 text-sm font-bold text-white transition hover:bg-cocoa/90 disabled:opacity-50"
              >
                {poolStatus === 'looking' ? 'Looking you up…' :
                 poolStatus === 'sending' ? 'Sending link…' :
                 'Enter The Pool →'}
              </button>
            </form>
          )}

          {poolStatus === 'not-found' && (
            <div className="text-xs text-cocoa/60 space-y-1 pt-1 border-t border-sand/50">
              <p>
                New here? <a href="/nominate" className="underline font-semibold hover:text-cocoa">Start with a nomination</a>.
              </p>
              <p>
                Other roles (sponsor, guardian, admin): use the role picker below.
              </p>
            </div>
          )}
        </div>

        <div className="flex items-center gap-3 text-xs text-cocoa/40">
          <div className="flex-1 border-t border-sand"></div>
          <span>or pick a role</span>
          <div className="flex-1 border-t border-sand"></div>
        </div>

        {mismatchMessage && (
          <div className="rounded-2xl border border-brick/20 bg-white/80 p-4 text-sm text-left text-cocoa space-y-3">
            <p>{mismatchMessage}</p>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              {safeCurrentRole && (
                <button type="button" onClick={handleReturnToPortal} className="btn w-full sm:w-auto">
                  Go to {formatRoleLabel(safeCurrentRole)} portal
                </button>
              )}
              <button
                type="button"
                onClick={handleSignOutNotice}
                className="btn w-full sm:w-auto bg-white text-cocoa border border-sand hover:bg-shell disabled:opacity-70"
                disabled={signingOut}
              >
                {signingOut ? 'Signing out…' : 'Sign out'}
              </button>
            </div>
          </div>
        )}

        <div className="space-y-3">
          <span className="text-sm font-medium text-cocoa block">1. Select your portal</span>
          <div className="bg-white/70 rounded-full p-1 grid grid-cols-3 gap-1">
            {roles.map(option => {
              const isActive = selectedRole === option.value
              const isDisabled = !portalIsOpen(option.value)
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleRoleSelect(option.value)}
                  disabled={isDisabled}
                  className={`px-3 py-2 rounded-full text-sm font-medium transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brick ${
                    isActive ? 'bg-brick text-white shadow-soft' : 'text-cocoa/70 hover:bg-shell'
                  } ${isDisabled ? 'cursor-not-allowed opacity-60 hover:bg-white/70' : ''}`}
                >
                  {option.label}
                </button>
              )
            })}
          </div>
        </div>

        <form className="space-y-5" onSubmit={handleSubmit}>
          <div className="space-y-2">
            <label htmlFor="email" className="text-sm font-medium text-cocoa">
              2. Enter your email
            </label>
            <input
              id="email"
              type="email"
              required
              autoComplete="email"
              className="w-full border border-sand bg-white rounded-xl px-3 py-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-mint/60 focus-visible:ring-2 focus-visible:ring-mint/40"
              value={email}
              onChange={event => setEmail(event.target.value)}
              placeholder="you@example.com"
            />
          </div>

          {selectedRole === 'admin' && (
            <div className="space-y-2">
              <label htmlFor="password" className="text-sm font-medium text-cocoa">
                Password
              </label>
              <input
                id="password"
                type="password"
                required
                autoComplete="current-password"
                className="w-full border border-sand bg-white rounded-xl px-3 py-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-mint/60 focus-visible:ring-2 focus-visible:ring-mint/40"
                value={password}
                onChange={event => setPassword(event.target.value)}
                placeholder="Your password"
              />
            </div>
          )}

          <div className="space-y-3">
            <button
              type="submit"
              className="w-full btn disabled:opacity-70"
              disabled={selectedRole === 'admin' ? passwordLoading : sendingLink}
            >
              {selectedRole === 'admin'
                ? passwordLoading
                  ? 'Signing in…'
                  : 'Sign in with Password'
                : sendingLink
                ? 'Sending magic link…'
                : 'Send sign-in link'}
            </button>
          </div>

          {enablePasskeys && selectedRole !== 'admin' && (
            <div className="space-y-3 rounded-2xl border border-mint/20 bg-white/80 p-4">
              <p className="text-sm text-cocoa/70">
                Prefer Face ID or device passkeys? Register once and sign in without waiting for an email.
              </p>
              <div className="flex flex-col gap-2 sm:flex-row">
                <button
                  type="button"
                  className="btn w-full sm:w-auto disabled:opacity-70"
                  onClick={handleRegisterPasskey}
                  disabled={registeringPasskey || passkeySigningIn || !email}
                >
                  {registeringPasskey ? 'Saving passkey…' : 'Register passkey'}
                </button>
                <button
                  type="button"
                  className="btn w-full sm:w-auto bg-mint text-cocoa hover:bg-mint/90 disabled:opacity-70"
                  onClick={handlePasskeySignIn}
                  disabled={passkeySigningIn || registeringPasskey || !email}
                >
                  {passkeySigningIn ? 'Signing in…' : 'Sign in with Face ID (passkey)'}
                </button>
              </div>
              {passkeyMessage && <p className="text-xs text-cocoa/60">{passkeyMessage}</p>}
            </div>
          )}
        </form>

        {profileError && (
          <div className="text-sm text-center text-brick">
            We had trouble loading your profile details. Please try again later.
          </div>
        )}
        {message && <div className="text-sm text-mint text-center">{message}</div>}
        {error && <div className="text-sm text-center text-brick">{error}</div>}
      </div>
    </div>
  )
}
